import { useListResources } from "@/hooks/useResources"
import { Button } from "@/components/ui/button"
import { Loader2, Database, Plus } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { ROUTES } from "@/routes/path"
import React from "react"
import resourceTypes from "@/lib/data/resources.json"

const resourceIcon = (resource: { type: string; name: string }) => {
  // Find the resource type in resources.json
  const resourceType = resourceTypes.find((rt) => rt.id === resource.type)

  if (resourceType?.icon) {
    return <img src={resourceType.icon} alt={resource.name} className="h-6 w-6" />
  }
  return <Database className="h-6 w-6 text-gray-400" />
}

export default function ListResources() {
  const { data: resources, isLoading } = useListResources("always")
  const navigate = useNavigate()

  const handleAddResource = () => {
    navigate(ROUTES.DASHBOARD.RESOURCES.CREATE)
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Compact Header */}
      <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-[#0F0F12]">
        <div className="mx-auto flex max-w-[1920px] items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Resources</h1>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Manage your resources and data connections.
            </p>
          </div>
          {resources && resources.length > 0 && (
            <Button size="sm" onClick={handleAddResource} className="h-7 gap-1.5 px-2.5 text-xs">
              <Plus className="h-3 w-3" />
              Add New
            </Button>
          )}
        </div>
      </div>

      {/* Cards Container */}
      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : resources && resources.length > 0 ? (
          <div className="mx-auto max-w-[1920px] p-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {resources.map((resource) => (
                <div
                  key={resource.id}
                  onClick={() => navigate(ROUTES.DASHBOARD.RESOURCES.MANAGE(resource.id))}
                  className="group relative cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-gray-300 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                      {resourceIcon(resource)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-white">{resource.name}</h3>
                      <p className="mt-0.5 text-xs capitalize text-gray-500 dark:text-gray-400">{resource.type}</p>
                    </div>
                  </div>

                  {resource.description && (
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
                      {resource.description}
                    </p>
                  )}

                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`h-1.5 w-1.5 rounded-full ${
                          resource.status === "active"
                            ? "bg-green-400"
                            : resource.status === "analyzing"
                              ? "bg-yellow-400"
                              : resource.status === "error"
                                ? "bg-red-400"
                                : "bg-gray-400"
                        }`}
                      />
                      <span className="text-xs capitalize text-gray-500 dark:text-gray-400">{resource.status}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(ROUTES.DASHBOARD.RESOURCES.MANAGE(resource.id))
                      }}
                      className="h-6 gap-1 px-2 text-xs"
                    >
                      Manage
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-16 flex flex-1 items-center justify-center">
            <div className="text-center">
              <Database className="mx-auto h-10 w-10 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No resources found</h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Get started by adding your first resource.
              </p>
              <Button size="sm" onClick={handleAddResource} className="mt-4 h-7 gap-1.5 px-2.5 text-xs">
                <Plus className="h-3 w-3" />
                Add Resource
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
